import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Backend-proxied image upload (with optional normalized crop box + target output ratio)
export async function uploadImage(file, crop, targetRatio) {
  const fd = new FormData();
  fd.append("file", file);
  const params = [];
  if (crop && crop.w > 0 && crop.h > 0) {
    params.push(`crop_x=${crop.x.toFixed(5)}`);
    params.push(`crop_y=${crop.y.toFixed(5)}`);
    params.push(`crop_w=${crop.w.toFixed(5)}`);
    params.push(`crop_h=${crop.h.toFixed(5)}`);
  }
  if (targetRatio && targetRatio > 0) {
    params.push(`target_ratio=${targetRatio.toFixed(5)}`);
  }
  const qs = params.length ? `?${params.join("&")}` : "";
  const headers = { "Content-Type": "multipart/form-data" };
  try {
    if (typeof window !== "undefined") {
      const adminToken = localStorage.getItem("zc_admin_token");
      const adminKey = localStorage.getItem("zc_admin_key");
      if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;
      else if (adminKey) headers["x-admin-key"] = adminKey;
    }
  } catch (e) { /* ignore */ }
  const r = await axios.post(`${API}/upload${qs}`, fd, { headers });
  return r.data.url.startsWith("http") ? r.data.url : `${BACKEND_URL}${r.data.url}`;
}

// Live drag-to-pan + zoom crop. Aspect ratio configurable (default 1:1 square).
export function CropModal({ open, src, busy, error, onCancel, onConfirm, aspectRatio = "1/1", outputLabel = "1200×1200 (1:1)" }) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1); // multiplier on top of cover scale
  const [pan, setPan] = useState({ x: 0, y: 0 }); // user offset on top of centered baseline
  const dragRef = useRef({ active: false, sx: 0, sy: 0, px: 0, py: 0 });

  // Track container size on mount/resize
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setContainerSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [open]);

  // Reset state when src changes
  useEffect(() => {
    if (!src) return;
    setZoom(1); setPan({ x: 0, y: 0 });
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  const baseScale = (() => {
    const { w, h } = containerSize;
    if (!w || !h || !natural.w || !natural.h) return 1;
    return Math.max(w / natural.w, h / natural.h);
  })();
  const scale = baseScale * zoom;
  const dispW = natural.w * scale;
  const dispH = natural.h * scale;

  // Centered baseline position
  const baseTx = (containerSize.w - dispW) / 2;
  const baseTy = (containerSize.h - dispH) / 2;
  // Clamp pan so image always covers container fully
  const minPanX = containerSize.w - dispW - baseTx; // = -dispW + containerSize.w - baseTx → simpler: clamp final Tx
  // We'll clamp via final Tx in [-dispW + containerSize.w, 0]
  const finalTxRaw = baseTx + pan.x;
  const finalTyRaw = baseTy + pan.y;
  const finalTx = Math.min(0, Math.max(containerSize.w - dispW, finalTxRaw));
  const finalTy = Math.min(0, Math.max(containerSize.h - dispH, finalTyRaw));

  const onDown = (e) => {
    e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    dragRef.current = { active: true, sx: pt.clientX, sy: pt.clientY, px: pan.x, py: pan.y };
  };
  const onMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - dragRef.current.sx;
    const dy = pt.clientY - dragRef.current.sy;
    setPan({ x: dragRef.current.px + dx, y: dragRef.current.py + dy });
  }, []);
  const onUp = useCallback(() => { dragRef.current.active = false; }, []);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [open, onMove, onUp]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setZoom((z) => Math.max(1, Math.min(4, z + delta)));
  };

  const submit = () => {
    if (!natural.w || !natural.h || !containerSize.w || !containerSize.h) {
      onConfirm(null);
      return;
    }
    // Compute normalized crop window in natural-image coordinates
    const leftInNat = (-finalTx) / scale;
    const topInNat = (-finalTy) / scale;
    const cropWnat = containerSize.w / scale;
    const cropHnat = containerSize.h / scale;
    const crop = {
      x: Math.max(0, leftInNat / natural.w),
      y: Math.max(0, topInNat / natural.h),
      w: Math.min(1, cropWnat / natural.w),
      h: Math.min(1, cropHnat / natural.h),
    };
    onConfirm(crop);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[140] bg-black/90 backdrop-blur-2xl flex items-center justify-center p-4" onClick={onCancel}>
      <div data-testid="crop-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-surface border border-gold/40 rounded-sm overflow-hidden">
        <div className="p-5">
          <div className="font-serif-accent text-gold text-sm">~ Drag &amp; zoom · {aspectRatio.replace("/", ":")} frame</div>
          <div className="font-display uppercase text-xl tracking-tight mt-1">Frame your shot</div>
          <div className="text-muted text-xs mt-2 leading-relaxed">Drag the photo to reposition. Use the slider or scroll wheel to zoom. The visible frame is what shows on your card. <span className="text-gold">📐 Output: {outputLabel}</span></div>

          <div
            ref={containerRef}
            data-testid="crop-frame"
            onMouseDown={onDown}
            onTouchStart={onDown}
            onWheel={onWheel}
            className="mt-4 relative w-full overflow-hidden rounded-sm border border-white/10 bg-black select-none cursor-grab active:cursor-grabbing"
            style={{ aspectRatio: aspectRatio, touchAction: "none" }}
          >
            {src && natural.w > 0 && (
              <img
                src={src}
                alt="preview"
                draggable={false}
                data-testid="crop-preview-img"
                className="absolute top-0 left-0 max-w-none pointer-events-none"
                style={{
                  width: `${dispW}px`,
                  height: `${dispH}px`,
                  transform: `translate3d(${finalTx}px, ${finalTy}px, 0)`,
                }}
              />
            )}
            {/* center-cross hint */}
            <div className="pointer-events-none absolute inset-0 border-2 border-white/10" />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-[10px] text-muted font-display uppercase tracking-[0.2em] w-12">Zoom</span>
            <input
              data-testid="crop-zoom-slider"
              type="range" min="1" max="4" step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-gold"
            />
            <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} type="button" className="text-[10px] text-muted hover:text-white font-display uppercase tracking-[0.2em]">Reset</button>
          </div>

          {error && (
            <div data-testid="crop-error" className="mt-3 p-2.5 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-xs">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mt-5">
            <button type="button" onClick={onCancel} className="py-3 border border-white/15 rounded-sm font-display uppercase text-[11px] tracking-[0.2em] text-muted hover:text-white">Re-pick</button>
            <button type="button" onClick={submit} disabled={busy} data-testid="crop-confirm-btn" className="py-3 bg-gold-grad text-bg rounded-sm font-display uppercase text-[11px] tracking-[0.2em] hover:brightness-110 disabled:opacity-50">
              {busy ? "Uploading…" : "Use this image"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Uploader with backend-proxied upload + cropper. Pass aspectRatio="21/8" for carousel banners.
export function ImageUploader({ value, onChange, placeholder = "Image URL or upload →", aspectRatio = "1/1" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cropOpen, setCropOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [cropPreview, setCropPreview] = useState("");

  // Aspect-ratio derived UI labels + backend target_ratio
  const ratioParts = aspectRatio.split("/").map(s => parseFloat(s.trim()));
  const ratioNum = (ratioParts[0] && ratioParts[1]) ? (ratioParts[0] / ratioParts[1]) : 1.0;
  const isWide = ratioNum > 1.5;
  const is16by9 = Math.abs(ratioNum - 16 / 9) < 0.02;
  const outputLabel = is16by9 ? "1920×1080 (16:9)" : (isWide ? "1680×640 (21:8)" : "1200×1200 (1:1)");
  const hint = is16by9
    ? "📐 16:9 wide · output 1920×1080 · max 10 MB · JPG/PNG/WEBP"
    : (isWide
        ? "📐 21:8 banner · output 1680×640 · max 10 MB · JPG/PNG/WEBP"
        : "📐 Square 1:1 · output 1200×1200 · max 10 MB · JPG/PNG/WEBP");

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Hard-reset all upload state so a previous failed/cancelled attempt can never
    // leave the uploader stuck. Without this, an upload that errored out kept the
    // old preview + cropFile in memory, so the next "Upload" tap would open the
    // crop modal but submitting again still failed because of stale state. Now
    // every new pick starts from a totally clean slate.
    if (cropPreview) {
      try { URL.revokeObjectURL(cropPreview); } catch (_) { /* noop */ }
    }
    setCropFile(f);
    setCropPreview(URL.createObjectURL(f));
    setCropOpen(true);
    setErr("");
    setBusy(false);
    // Clear the input so picking the SAME file again still triggers onChange.
    e.target.value = "";
  };

  const confirmUpload = async (crop) => {
    if (!cropFile) return;
    setBusy(true); setErr("");
    try {
      const url = await uploadImage(cropFile, crop, ratioNum);
      onChange(url);
      setCropOpen(false);
      setCropFile(null);
      if (cropPreview) URL.revokeObjectURL(cropPreview);
      setCropPreview("");
    } catch (ex) {
      const status = ex?.response?.status;
      let msg = ex?.response?.data?.detail || ex.message || "Upload failed";
      if (status === 413) {
        msg = "That image is too big. Pick a smaller file (max 10 MB).";
      } else if (status === 401 || status === 403) {
        msg = "Your admin session expired. Sign out and back in, then retry.";
      } else if (!status) {
        // Network / Cloudflare blip — retry-friendly hint
        msg = `Network hiccup: ${msg}. Tap Re-pick and try again.`;
      }
      setErr(msg);
    }
    setBusy(false);
  };

  const closeCropper = () => {
    // Single source of truth for closing — used by cancel button, error retry,
    // or successful upload. Always resets every piece of state.
    setCropOpen(false);
    setCropFile(null);
    if (cropPreview) {
      try { URL.revokeObjectURL(cropPreview); } catch (_) { /* noop */ }
    }
    setCropPreview("");
    setErr("");
    setBusy(false);
  };

  return (
    <div>
      <div className="flex items-stretch gap-2">
        {value && (
          <div data-testid="cloudinary-preview" className="w-14 h-12 shrink-0 rounded-sm overflow-hidden border border-white/10 bg-cover bg-center" style={{ backgroundImage: `url('${value}')` }} />
        )}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 bg-bg border border-white/10 p-3 rounded-sm text-sm" />
        <label data-testid="cloudinary-upload-label" className={`px-4 flex items-center justify-center bg-gold/15 border border-gold/40 rounded-sm font-display uppercase text-[10px] tracking-[0.2em] text-gold cursor-pointer hover:bg-gold/25 ${busy ? "opacity-60" : ""}`}>
          {busy ? "…" : "Upload"}
          <input type="file" accept="image/*" onChange={onPick} className="hidden" disabled={busy} data-testid="cloudinary-upload-input" />
        </label>
        {value && (
          <button type="button" data-testid="cloudinary-clear" onClick={() => onChange("")} className="px-3 text-muted hover:text-red-400 text-sm">×</button>
        )}
      </div>
      {err && <div className="text-red-400 text-xs mt-1" data-testid="upload-error">{err}</div>}
      <div className="text-[10px] text-muted font-serif-accent mt-1">{hint}</div>
      <CropModal
        open={cropOpen}
        src={cropPreview}
        busy={busy}
        error={err}
        aspectRatio={aspectRatio}
        outputLabel={outputLabel}
        onCancel={closeCropper}
        onConfirm={confirmUpload}
      />
    </div>
  );
}
